import { next, once, schedule } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { InferPopupData, PopupData, PopupInnerComponentClass, Service } from 'webapp-types';
import { PopupCloseReason, PopupState } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { arePopupsAllowed } from 'webapp/utils/common';

import CommonPopupsConfirmPopupComponent from 'webapp/components/common/popups/confirm';
import PopupListComponent from 'webapp/components/common/popups/popup-list';

import type Popup from 'webapp/models-custom/popup/base';
import type { PopupContextProps } from 'webapp/models-custom/popup/context';
import PopupContext from 'webapp/models-custom/popup/context';
import type { PopupModalProps } from 'webapp/models-custom/popup/modal';
import PopupModal from 'webapp/models-custom/popup/modal';
import PopupList from 'webapp/models-custom/popup/popup-list';

// Distributes over unions: true only if every union member has no keys
type AllMembersEmpty<T> = T extends unknown ? (keyof T extends never ? true : false) : never;

type PopupDataArgs<TData> = string extends keyof TData
	? [data?: TData] // PopupData.Any (any key allowed) — optional
	: [AllMembersEmpty<TData>] extends [true]
		? [data?: never] // PopupData.None (no known keys) — no data allowed
		: Record<string, never> extends TData
			? [data?: TData] // All properties optional — optional
			: [data: TData]; // Has required properties — required

export interface TypedPopupModal<TData extends object> extends PopupModal {
	open(...args: PopupDataArgs<TData>): TypedPopupModal<TData> | void;
	data: TData;
}

export interface TypedPopupContext<TData extends object> extends PopupContext {
	open(...args: PopupDataArgs<TData>): TypedPopupContext<TData> | void;
	data: TData;
}

export default class PopupService extends EmberService {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked popupStack: Popup[] = [];

	// temporary flag to close all popup closing on outside click
	// used in select-with-popup-as-dropdown bcos select2 opening event
	// triggers way before click is released, which closes the newly opened "dropdown"
	// the click event has a "body" target so we cannot catch it any other way
	blockCloseOnOutsideClick = false;

	get popupsActive(): Popup[] {
		return this.popupStack.filter((item) => item.isOpen);
	}

	get activePopup(): Popup | undefined {
		return this.popupsActive.slice(-1)[0];
	}

	get anyPopupsOpen(): boolean {
		return this.popupStack.length > 0;
	}

	open(popupCM: Popup, data: object = {}): Popup | void {
		if (!popupCM || !arePopupsAllowed()) {
			return;
		}

		if (
			this.flowTourOnboardingService.isBannerShown &&
			!this.flowTourOnboardingService.isEligibleToOpenPopup(popupCM, data)
		) {
			return;
		}

		// do not open a popup that is already open
		// unless explicitly allowed to do so (allowMultipleInstances flag)
		if (!popupCM.allowMultipleInstances) {
			const otherInstance = this.findByComponent(popupCM.componentInnerClass);

			if (otherInstance && otherInstance !== popupCM) {
				if (otherInstance.isOpen || otherInstance.isOpening) {
					this.loggerService.logToConsole('[Popups] Block opening multiple same', {
						name: popupCM.componentInnerClass.toString(),
					});
					return;
				} else if (otherInstance.isClosing) {
					// the same popup is closing - we need to wait
					// before trying open again
					void this.afterPopupStatesSettled().then(() => {
						this.open(popupCM, data);
					});
					return popupCM;
				}
			}
		}

		// do not allow to modify this user-passed data
		Object.freeze(data);

		popupCM.data = data;
		popupCM.state = PopupState.OPENING;

		// if currently active popup explicitly does not allow others to stack on it, unload it first
		const topPopup = this.popupStack.slice(-1)[0];
		if (topPopup && !topPopup.allowStacked) {
			void topPopup.close(PopupCloseReason.NOSTACKED);
		}

		this.popupStack = [...this.popupStack, popupCM];

		const promise = this.afterPopupStatesSettled();

		void promise.then(() => {
			this.renderPopup(popupCM);
		});

		popupCM.statePromise = promise;

		return popupCM;
	}

	close(popupCM = this.activePopup): Promise<unknown> {
		let promise;

		if (popupCM) {
			promise = this.removePopup(popupCM);
		} else {
			promise = Promise.resolve();
		}

		return promise;
	}

	closeAll(): Promise<unknown> {
		this.popupStack.forEach((popup) => {
			void popup.close(PopupCloseReason.CLOSEALL);
		});

		const promise = this.checkToTriggerAllPopupsClosedEvent();

		return promise;
	}

	closeOnlyContextPopups(): void {
		this.popupStack.forEach((popupModel) => {
			if (popupModel.isContext) {
				void popupModel.close(PopupCloseReason.ONLYCONTEXT);
			}
		});
	}

	repositionAllPopups(): void {
		window.dispatchEvent(new Event('resize'));
	}

	createModal<C extends PopupInnerComponentClass>(
		component: C,
		otherOptions: Omit<PopupModalProps, 'component'> = {},
	): TypedPopupModal<InferPopupData<C>> {
		return new PopupModal(Object.assign({ component }, otherOptions)) as TypedPopupModal<
			InferPopupData<C>
		>;
	}

	createContext<C extends PopupInnerComponentClass>(
		component: C,
		attachTo: HTMLElement,
		otherOptions: Omit<PopupContextProps, 'component' | 'attachTo'> = {},
	): TypedPopupContext<InferPopupData<C>> {
		return new PopupContext(
			Object.assign({ component, attachTo }, otherOptions),
		) as TypedPopupContext<InferPopupData<C>>;
	}

	isOpen(component: PopupInnerComponentClass): boolean {
		const popupsOpenOrOpening = this.popupStack.filter((popup) => {
			return popup.isOpen || popup.isOpening;
		});
		return !!this.findByComponentInPopups(component, popupsOpenOrOpening);
	}

	// the popup carries the component's popup data, as with createModal/createContext
	findByComponent<C extends PopupInnerComponentClass>(
		component: C,
	): (Popup & { data: InferPopupData<C> }) | null {
		const popup = this.findByComponentInPopups(component, this.popupStack);
		return (popup as (Popup & { data: InferPopupData<C> }) | undefined) || null;
	}

	findContextByComponentAndAttachTo(
		component: PopupInnerComponentClass,
		attachTo: HTMLElement,
	): PopupContext | null {
		const alreadyOpenPopup = this.findByComponent(component) as PopupContext | null;
		if (alreadyOpenPopup?.attachTo === attachTo) {
			return alreadyOpenPopup;
		}
		return null;
	}

	private findByComponentInPopups(
		component: PopupInnerComponentClass,
		popups: Popup[],
	): Popup | null {
		return popups.find((item) => item.componentInnerClass === component) || null;
	}

	private renderPopup(popupCM: Popup): void {
		schedule('afterRender', () => {
			if (popupCM.isOpening) {
				popupCM.state = PopupState.OPEN;
			}
			this.checkForBodyOvelayClass();
		});
	}

	private removePopup(popupCM: Popup): Promise<void> {
		popupCM.state = PopupState.CLOSING;

		let promise = popupCM.readyToClose();

		promise = promise.then(() => {
			popupCM.state = PopupState.INACTIVE;
			popupCM.statePromise = null;

			popupCM.cleanup();
			this.popupStack = this.popupStack.filter((p) => p !== popupCM);

			void this.checkToTriggerAllPopupsClosedEvent();
			this.checkForBodyOvelayClass();
		});

		popupCM.statePromise = promise;

		return promise;
	}

	private afterPopupStatesSettled(): Promise<unknown> {
		// wait for the rest of the popups to settle (open, close, ..)
		const promises: Promise<unknown>[] = [];
		this.popupStack.forEach((item) => {
			if (item.statePromise) {
				promises.push(item.statePromise);
			}
		});
		return Promise.allSettled(promises);
	}

	private checkToTriggerAllPopupsClosedEvent(): Promise<void> {
		const promise = this.afterPopupStatesSettled()
			.then(() => {
				next(this, () => {
					if (!this.anyPopupsOpen) {
						once(this, this.triggerAllPopupsClosedEvent);
					}
				});
			})
			.catch();

		return promise;
	}

	private triggerAllPopupsClosedEvent(): void {
		this.eventManagerService.trigger(AppEvent.ALL_POPUPS_CLOSED);
	}

	private checkForBodyOvelayClass(): void {
		const overlayClass = 'modal-has-overlay',
			bodyNode = document.body,
			anyModalWithOverlay = this.popupStack.some((popup) => {
				return popup.isModal && (popup as PopupModal).hasOverlay;
			});

		if (anyModalWithOverlay) {
			// calculating scrollbar width to check if exists so we can prevent jumping of content
			if (!bodyNode.classList.contains(overlayClass)) {
				const scrollWidth = window.innerWidth - bodyNode.clientWidth;

				if (scrollWidth > 0) {
					bodyNode.style.paddingRight = `${scrollWidth}px`;
				}

				bodyNode.classList.add(overlayClass);
			}
		} else {
			if (bodyNode.classList.contains(overlayClass)) {
				bodyNode.classList.remove(overlayClass);
				bodyNode.style.paddingRight = '';
			}
		}
	}

	openConfirmPopup(props: PopupData.Common.Confirm): PopupModal {
		const popup = this.createModal(CommonPopupsConfirmPopupComponent, {
			popupClass: `popup-type-confirm ${props.isWarning ? 'warning' : ''}`,
			showClose: false,
		});
		popup.open(props);
		return popup;
	}

	// should be used only when using checkbox to not show again the popup
	checkToOpenConfirmPopup(props: PopupData.Common.Confirm): Promise<boolean> {
		const dontShowAgainKey = props.dontShowAgainKey;
		if (!dontShowAgainKey) {
			return Promise.resolve(true);
		}

		const dontShowAgainValue =
			this.userSettingsService.getConfirmPopupDontShowAgainValue(dontShowAgainKey);

		if (dontShowAgainValue) {
			return Promise.resolve(true);
		}

		return new Promise((resolve) => {
			this.openConfirmPopup({
				...props,
				onConfirm: () => {
					props.onConfirm?.();
					resolve(true);
				},
				onReject: () => {
					props.onReject?.();
					resolve(false);
				},
			});
		});
	}

	createListPopup(
		attachTo?: HTMLElement | null,
		theme = '',
		extraClass = '',
		allowStacked = false,
		allowMultipleInstances = false,
		inheritWidth = false,
		neverFullwidth = false,
		offset?: number,
	) {
		// this is here just bcos it's an extremely commonly used popup

		if (!attachTo) {
			return;
		}

		const isInstanceOpen = this.isOpen(PopupListComponent);

		if (isInstanceOpen && !allowMultipleInstances) {
			return;
		}

		const popup = new PopupList({
			attachTo,
			theme,
			popupClass: `optionspopup ${extraClass}`,
			allowStacked, // will be true, for designer onboarding tour bubbles
			allowMultipleInstances,
			inheritWidth,
			neverFullwidth,
			offset,
		});

		return popup;
	}
}

declare module '@ember/service' {
	interface Registry {
		popup: PopupService;
	}
}
